"use client";

/**
 * useDeliveryAddressCoverage — React Query hook for delivery coverage preflight.
 *
 * Given a restaurant identifier and a complete delivery address, fires
 * `POST /api/v1/orders/delivery-coverage` to validate that the courier can
 * deliver to the address — before the customer adds items or proceeds to
 * checkout. The intent is that unserviceable addresses are shown immediately
 * at address-entry time, so the customer never encounters a post-payment
 * failure.
 *
 * Debounce: waits 600 ms after the address changes before firing the request,
 * to avoid hammering the API while the user is mid-typing.
 *
 * Status lifecycle:
 *   'idle'          — address incomplete or delivery not selected
 *   'checking'      — debounce timer ticking or request in-flight
 *   'deliverable'   — courier can deliver (feeCents + etaMinutes available)
 *   'not_deliverable' — courier cannot deliver (reason + message available)
 *   'error'         — unexpected API/network error (optimistic: don't block)
 */

import { useEffect, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { IOrderDeliveryAddressComponents } from '@order-up/models';
import { checkoutService } from '../services/checkout';
import type { DeliveryCoverageResult } from '../services/checkout';

// ── Query key factory ──────────────────────────────────────────────────────

export const deliveryCoverageKeys = {
  all: ['deliveryCoverage'] as const,
  check: (
    locationNumericId: number,
    tenantId: number,
    addr: IOrderDeliveryAddressComponents,
    orderValueCents?: number,
  ) =>
    [
      ...deliveryCoverageKeys.all,
      'check',
      { restaurantId: locationNumericId, tenantId, addr, orderValueCents },
    ] as const,
};

// ── isDeliveryAddressComplete (mirrors DeliveryAddressForm export) ─────────

function isComplete(a?: IOrderDeliveryAddressComponents): boolean {
  return !!(
    a &&
    a.line1?.trim() &&
    a.city?.trim() &&
    a.state?.trim() &&
    a.postalCode?.trim()
  );
}

// ── Result shape ──────────────────────────────────────────────────────────

export type DeliveryCoverageStatus =
  | 'idle'
  | 'checking'
  | 'deliverable'
  | 'not_deliverable'
  | 'error';

export interface DeliveryCoverageState {
  status: DeliveryCoverageStatus;
  feeCents?: number;
  etaMinutes?: number;
  reason?: string;
  message?: string;
}

// ── Hook ─────────────────────────────────────────────────────────────────

interface UseDeliveryAddressCoverageParams {
  /** Numeric restaurant id (used by the orders API). */
  restaurantId: number | undefined;
  /** Numeric tenant id. */
  tenantId: number | undefined;
  /** Whether delivery is currently selected (guard: skip when pickup). */
  isDelivery: boolean;
  /** The structured address the customer has entered. */
  deliveryAddressComponents: IOrderDeliveryAddressComponents | undefined;
  /**
   * Optional order value in cents (used by some providers to price the quote).
   * Can be omitted for the preflight — a 0-cent check is still a valid
   * serviceability check.
   */
  orderValueCents?: number;
  /** Debounce delay in ms (default 600). */
  debounceMs?: number;
}

export function useDeliveryAddressCoverage({
  restaurantId: locationNumericId,
  tenantId,
  isDelivery,
  deliveryAddressComponents,
  orderValueCents,
  debounceMs = 600,
}: UseDeliveryAddressCoverageParams): DeliveryCoverageState {
  // Debounced copy of the address — the query key is keyed on this, not the
  // raw address, so TanStack Query doesn't fire until the user pauses.
  const [debouncedAddr, setDebouncedAddr] =
    useState<IOrderDeliveryAddressComponents | undefined>(
      deliveryAddressComponents,
    );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setDebouncedAddr(deliveryAddressComponents);
    }, debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [deliveryAddressComponents, debounceMs]);

  const addressComplete = isComplete(debouncedAddr);
  const enabled =
    isDelivery &&
    !!locationNumericId &&
    !!tenantId &&
    addressComplete;

  const query = useQuery<DeliveryCoverageResult, Error>({
    queryKey: deliveryCoverageKeys.check(
      locationNumericId ?? 0,
      tenantId ?? 0,
      debouncedAddr ?? {},
      orderValueCents,
    ),
    queryFn: () =>
      checkoutService.checkDeliveryCoverage({
        restaurantId: locationNumericId!,
        tenantId: tenantId!,
        deliveryAddressComponents: debouncedAddr!,
        ...(orderValueCents !== undefined ? { orderValueCents } : {}),
      }),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 min — Drive quotes expire in 5 min
    retry: false, // non-retryable; a coverage failure is not transient
    gcTime: 5 * 60 * 1000,
  });

  // Pending (debounce or fetch) — address is complete but result not yet known.
  const isDebouncing =
    isDelivery &&
    addressComplete &&
    // raw address changed but debounced hasn't caught up yet
    JSON.stringify(deliveryAddressComponents) !== JSON.stringify(debouncedAddr);

  if (!isDelivery || !locationNumericId || !tenantId) {
    return { status: 'idle' };
  }

  if (!addressComplete) {
    return { status: 'idle' };
  }

  if (isDebouncing || query.isLoading || query.isFetching) {
    return { status: 'checking' };
  }

  if (query.isError) {
    return { status: 'error' };
  }

  if (query.data) {
    if (query.data.deliverable) {
      return {
        status: 'deliverable',
        feeCents: query.data.feeCents,
        etaMinutes: query.data.etaMinutes,
      };
    }
    return {
      status: 'not_deliverable',
      reason: query.data.reason,
      message: query.data.message,
    };
  }

  // Query enabled but no data yet (between enabled becoming true and first fetch)
  return { status: 'checking' };
}
